import { HttpBackend, HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, filter as rxFilter, firstValueFrom, map } from 'rxjs';
import { ApiBaseUrls, ApiControllers } from '../constants/constants';
import { DocumentsConstants } from '../constants/documents.constants';
import { DocumentsMessagingConstants } from '../constants/messaging.constants';
import { DocumentNodeType, DocumentPermission } from '../enums/data.enums';
import { DocumentListSortOptions, DocumentSortOptions, FolderSortOptions } from '../enums/sort-options.enums';
import { DocumentFilter } from '../filters/document.filter';
import { FolderFilter } from '../filters/folder.filter';
import {
    BulkActionResultModel,
    BulkChangeFolderPermissionRequest,
    BulkMoveDocumentsRequest,
    BulkPrepareDocumentUploadRequest,
    CommitDocumentUploadRequest,
    CreateFolderRequest,
    DocumentBulkActionRequest,
    DocumentDownloadModel,
    DocumentModel,
    DocumentNodeModel,
    DocumentStorageUsageModel,
    DocumentUploadReservationModel,
    EmptyTrashRequest,
    FileQueryCriteria,
    FolderBulkActionRequest,
    FolderModel,
    PrepareDocumentUploadRequest,
    UpdateDocumentRequest,
    UpdateFolderRequest
} from '../models/data/document.model';
import { AuditLog } from '../models/service/audit-log.model';
import { DocumentCenterAuditLogModel } from '../models/data/document-center-audit-log.model';
import { PaginatedListModel } from '../models/service/paginated-list.model';
import { PaginationResponseHeader } from '../models/service/pagination-response-header.model';
import { ServiceResultModel } from '../models/service/service-result.model';
import { APISanitizerUtilities } from '../utilities/api-sanitizer.utilities';
import { AuditLogMapper } from '../utilities/audit-log-mapper.utilities';
import { DocumentUtilities } from '../utilities/document.utilities';
import { ServiceUtilities } from '../utilities/service.utilities';
import { CacheService } from './cache.service';
import { UserSessionService } from './user-session.service';

/** Emitted by the direct-to-blob PUT: upload progress (0-100) and a terminal `done` flag. */
export interface BlobUploadProgress {
    progress: number;
    done: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class DocumentService {

    /** Bumped on every folder mutation so consumers using `effect()` re-fetch. */
    public version = signal<number>(0);

    // Folder cache — populated by API responses, source of truth for traversal/collision-check.
    private folderCache = new Map<number, FolderModel>();

    private apiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.FOLDERS()}`;
    private documentsApiPath: string = `${ApiBaseUrls.CORE()}${ApiControllers.DOCUMENTS()}`;

    // Interceptor-free client for the direct-to-blob PUT, so our Auth0 JWT is never sent to Azure.
    private blobHttpClient: HttpClient;

    constructor(private httpClient: HttpClient, private userSessionService: UserSessionService, httpBackend: HttpBackend, private cacheService: CacheService) {
        this.blobHttpClient = new HttpClient(httpBackend);
    }

    /**
     * Force a fresh fetch of file data by evicting the documents HTTP cache, then bumping the version
     * so version-driven effects re-run. Used to pick up scan-status transitions (Pending → Available
     * / Quarantined) that completed server-side after the page last loaded — without this the 60s
     * filter cache would keep serving the stale Pending rows.
     */
    public refreshNow(): void {
        this.cacheService.clearByPattern(/\/documents(?:[\/?]|$)/i);
        this.touch();
    }

    //#region Folder API

    /** POST /folders */
    public async createFolder(request: CreateFolderRequest): Promise<ServiceResultModel<FolderModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.post<FolderModel>(
                this.apiPath,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<CreateFolderRequest>(request)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            const folder = response.body as FolderModel;
            this.folderCache.set(folder.folderId, folder);
            this.touch();
            return {
                success: true,
                data: folder,
                message: DocumentsMessagingConstants.DOCUMENTS_CREATE_FOLDER_SUCCESS().replaceValuesWithObject({ name: folder.name }),
                statusCode: response.status
            };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /** PATCH /folders/{folderId} — rename + move + permission change in one request. */
    public async updateFolder(folderId: number, request: UpdateFolderRequest): Promise<ServiceResultModel<FolderModel>> {
        try {
            // Cross-container cap pre-check — fast-fail before the API rejects. The cache must be
            // warm for this to work; the page loads the folder tree on mount so it usually is.
            const current = this.folderCache.get(folderId);
            if (current && this.crossesPublicBoundary(current.permission, request.permission)) {
                const descendantFileCount = this.countDescendantFiles(folderId);
                if (descendantFileCount > DocumentsConstants.CROSS_CONTAINER_ITEM_CAP()) {
                    return {
                        success: false,
                        message: DocumentsMessagingConstants.DOCUMENTS_CROSS_CONTAINER_CAP_EXCEEDED().replaceValuesWithObject({
                            itemCount: descendantFileCount,
                            cap: DocumentsConstants.CROSS_CONTAINER_ITEM_CAP()
                        }),
                        statusCode: 422
                    };
                }
            }

            const response = await firstValueFrom(this.httpClient.patch<FolderModel>(
                `${this.apiPath}/${folderId}`,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateFolderRequest>(request)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            const folder = response.body as FolderModel;
            this.folderCache.set(folder.folderId, folder);
            this.touch();
            return {
                success: true,
                data: folder,
                message: DocumentsMessagingConstants.DOCUMENTS_RENAME_SUCCESS().replaceValuesWithObject({ name: folder.name }),
                statusCode: response.status
            };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /**
     * Move a folder to a new parent and delegate to PATCH. The folder inherits the destination's
     * permission (AdminOnly at the root); if a non-root destination can't be resolved the move fails
     * rather than silently applying the wrong permission. `sourceName` is the caller's known name for
     * the folder — used for the request's required `name` field and for a friendly not-found message
     * instead of an opaque id. The API auto-suffixes on sibling collision, so the returned folder's
     * name is authoritative.
     */
    public async moveFolder(folderId: number, destinationParentId: number | undefined, sourceName?: string): Promise<ServiceResultModel<FolderModel>> {
        try {
            // The move needs the source's current name. Prefer the warm cache, but fall back to a
            // fresh GET rather than failing — the cache is repopulated from root-level listings, so a
            // just-created or nested folder may legitimately not be cached at click time.
            let source = this.folderCache.get(folderId);
            if (!source) {
                const fetched = await this.getFolderById(folderId);
                if (!fetched.success || !fetched.data) {
                    return {
                        success: false,
                        // Show the folder by name (the caller knows it) — an opaque id means nothing to the user.
                        message: DocumentsMessagingConstants.DOCUMENTS_FOLDER_NOT_FOUND().replaceValuesWithObject({
                            name: sourceName ?? `#${folderId}`
                        }),
                        statusCode: fetched.statusCode ?? 404
                    };
                }
                source = fetched.data;
            }

            // Circular dependency guard.
            if (destinationParentId !== undefined) {
                if (destinationParentId === folderId) {
                    return { success: false, message: DocumentsMessagingConstants.DOCUMENTS_MOVE_INTO_SELF(), statusCode: 422 };
                }
                const descendants = this.getDescendantFolderIds(folderId);
                if (descendants.has(destinationParentId)) {
                    return { success: false, message: DocumentsMessagingConstants.DOCUMENTS_MOVE_INTO_DESCENDANT(), statusCode: 422 };
                }
            }

            // Moved folders inherit the destination's permission (AdminOnly at the root). For a
            // non-root destination, resolve its permission from the cache or a fresh fetch; if it
            // still can't be resolved, FAIL rather than defaulting to AdminOnly — defaulting would
            // silently change the moved folder's permission (e.g. moving into a Public folder).
            let destinationPermission = DocumentPermission.AdminOnly;
            if (destinationParentId !== undefined) {
                let destination = this.folderCache.get(destinationParentId);
                if (!destination) {
                    const fetchedDestination = await this.getFolderById(destinationParentId);
                    if (fetchedDestination.success && fetchedDestination.data) {
                        destination = fetchedDestination.data;
                    }
                }
                if (!destination) {
                    return {
                        success: false,
                        message: DocumentsMessagingConstants.DOCUMENTS_MOVE_DESTINATION_NOT_FOUND(),
                        statusCode: 404
                    };
                }
                destinationPermission = destination.permission;
            }

            return await this.updateFolder(folderId, {
                name: source.name,
                parentFolderId: destinationParentId,
                permission: destinationPermission
            });
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /** DELETE /folders/{folderId} — soft-delete with optional audit note. */
    public async deleteFolder(folderId: number, note?: string): Promise<ServiceResultModel<FolderModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.delete<FolderModel>(
                `${this.apiPath}/${folderId}`,
                {
                    body: note ? JSON.stringify(note) : null,
                    observe: 'response',
                    headers: DocumentsConstants.JSON_HEADERS()
                }
            ));
            const folder = response.body as FolderModel;
            this.folderCache.delete(folderId);
            this.touch();
            return { success: true, data: folder, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /** GET /folders/{folderId} */
    public async getFolderById(folderId: number): Promise<ServiceResultModel<FolderModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.get<FolderModel>(
                `${this.apiPath}/${folderId}`,
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            const folder = response.body as FolderModel;
            this.folderCache.set(folderId, folder);
            return { success: true, data: folder, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /** POST /folders/filter — one page; pagination via the x-pagination header. */
    public async filterFolders(filter: FolderFilter, skip: number = 0, take: number = DocumentsConstants.FOLDER_PAGE_SIZE(), orderBy: FolderSortOptions = FolderSortOptions.NameAsc): Promise<ServiceResultModel<PaginatedListModel<FolderModel>>> {
        try {
            const url = `${this.apiPath}/filter?orderBy=${encodeURIComponent(orderBy)}&skip=${skip}&take=${take}`;
            const response = await firstValueFrom(this.httpClient.post<FolderModel[]>(
                url,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<FolderFilter>(filter)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            const folders = response.body ?? [];
            for (const folder of folders) {
                this.folderCache.set(folder.folderId, folder);
            }
            const pagination = this.parsePaginationHeader(response.headers.get('x-pagination'));
            const paginatedList: PaginatedListModel<FolderModel> = {
                data: folders,
                total: pagination?.total,
                skip: pagination?.skip,
                take: pagination?.take,
                hasPrevious: pagination?.hasPrevious,
                hasNext: pagination?.hasNext ?? false
            };
            return { success: true, data: paginatedList, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /**
     * Eager-fetch every folder matching the filter by paging through FOLDER_PAGE_SIZE at a time
     * until the API reports no more. Folders are not infinite-scrolled — the whole set is loaded
     * up front and rendered together.
     */
    private async fetchAllFolders(filter: FolderFilter, orderBy: FolderSortOptions): Promise<ServiceResultModel<FolderModel[]>> {
        const all: FolderModel[] = [];
        const take = DocumentsConstants.FOLDER_PAGE_SIZE();
        let skip = 0;
        for (;;) {
            const result = await this.filterFolders(filter, skip, take, orderBy);
            if (!result.success || !result.data) {
                return { success: false, message: result.message, statusCode: result.statusCode };
            }
            const page = result.data.data ?? [];
            all.push(...page);
            if (!result.data.hasNext || page.length === 0) break;
            skip += page.length;
        }
        return { success: true, data: all, statusCode: 200 };
    }

    /**
     * PATCH /folders/bulk/permissions — change the permission of one or more ROOT folders in a single
     * request, cascading to every descendant. Non-root ids are silently skipped by the API. A change
     * that crosses the Public boundary triggers per-blob copy+delete and may be rejected (422) above
     * the cross-container cap — `extractApiMessage` surfaces the server's reason. Touches so the page
     * re-fetches the (re-badged) folders.
     */
    public async bulkChangeFolderPermissions(request: BulkChangeFolderPermissionRequest): Promise<ServiceResultModel<BulkActionResultModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.patch<BulkActionResultModel>(
                `${this.apiPath}/bulk/permissions`,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkChangeFolderPermissionRequest>(request)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            this.touch();
            return { success: true, data: response.body as BulkActionResultModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    /**
     * DELETE /folders/bulk — soft-delete a selection of folders, recursively soft-deleting their
     * contained subfolders and files. Locked folders (and their contents) are silently skipped rather
     * than rejected — the result's `totalSkipped` reflects them. Touches so the page re-fetches.
     */
    public async bulkDeleteFolders(request: FolderBulkActionRequest): Promise<ServiceResultModel<BulkActionResultModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.delete<BulkActionResultModel>(
                `${this.apiPath}/bulk`,
                {
                    body: JSON.stringify(APISanitizerUtilities.sanitizePayload<FolderBulkActionRequest>(request)),
                    observe: 'response',
                    headers: DocumentsConstants.JSON_HEADERS()
                }
            ));
            this.touch();
            return { success: true, data: response.body as BulkActionResultModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    //#endregion Folder API

    //#region Document (file) upload + listing API

    /** POST /documents/upload/prepare — reserve a direct-to-blob upload and get a SAS URL. */
    public async prepareUpload(request: PrepareDocumentUploadRequest): Promise<ServiceResultModel<DocumentUploadReservationModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.post<DocumentUploadReservationModel>(
                `${this.documentsApiPath}/upload/prepare`,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<PrepareDocumentUploadRequest>(request)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            return { success: true, data: response.body as DocumentUploadReservationModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    /**
     * POST /documents/upload/prepare-bulk — reserve a batch of direct-to-blob uploads into one folder
     * in a single round trip. Returns one reservation (SAS URL + resolved name) per file, in request
     * order. All-or-nothing: an oversized file or a batch total exceeding the org's remaining storage limit
     * rejects the entire batch (422) and creates no reservations.
     */
    public async bulkPrepareUpload(request: BulkPrepareDocumentUploadRequest): Promise<ServiceResultModel<DocumentUploadReservationModel[]>> {
        try {
            const response = await firstValueFrom(this.httpClient.post<DocumentUploadReservationModel[]>(
                `${this.documentsApiPath}/upload/prepare-bulk`,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkPrepareDocumentUploadRequest>(request)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            return { success: true, data: response.body ?? [], statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    /** POST /documents/upload/commit — finalize the Document row after the bytes are in blob storage. */
    public async commitUpload(request: CommitDocumentUploadRequest): Promise<ServiceResultModel<DocumentModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.post<DocumentModel>(
                `${this.documentsApiPath}/upload/commit`,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<CommitDocumentUploadRequest>(request)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            this.touch();
            return { success: true, data: response.body as DocumentModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    /**
     * PATCH /documents/upload/reservations/{reservationId}/expire — force-expire a pending reservation
     * whose upload was cancelled/abandoned, freeing its reserved storage immediately rather than waiting
     * for the server's expiry job. Best-effort: a 404/409 (already gone or committed) is not actionable.
     */
    public async expireUploadReservation(reservationId: number): Promise<ServiceResultModel<void>> {
        try {
            const response = await firstValueFrom(this.httpClient.patch(
                `${this.documentsApiPath}/upload/reservations/${reservationId}/expire`,
                null,
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            return { success: true, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /**
     * Pull the most specific human-readable message out of an API error so the user sees exactly
     * what was wrong (e.g. "Declared size must be greater than 0") instead of a generic failure.
     * Handles FluentValidation problem-details (`errors` map), `detail`, and `title`.
     */
    private extractApiMessage(error: any): string | undefined {
        const body = error?.error;
        if (!body) return undefined;
        if (typeof body === 'string') return body.trim() || undefined;
        if (body.errors && typeof body.errors === 'object') {
            const firstField = Object.keys(body.errors)[0];
            const firstMessage = firstField ? body.errors[firstField]?.[0] : undefined;
            if (firstMessage) return firstMessage;
        }
        return body.detail || body.title || undefined;
    }

    /** Shared failure result for the methods that require an active organization to be selected. */
    private noActiveOrganizationError<T>(): ServiceResultModel<T> {
        return { success: false, message: DocumentsMessagingConstants.DOCUMENTS_NO_ACTIVE_ORGANIZATION(), statusCode: 400 };
    }

    /**
     * PUT the file bytes straight to the Azure Blob SAS URL using an interceptor-free client (so our
     * JWT is never sent to Azure). Emits upload progress (0-100) then a terminal `done`. Cancellation
     * is by unsubscribing; transient-retry is the caller's responsibility (the upload manager).
     */
    public uploadFileToBlob(uploadUrl: string, file: File, contentType: string): Observable<BlobUploadProgress> {
        return this.blobHttpClient.put(uploadUrl, file, {
            headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType },
            observe: 'events',
            reportProgress: true
        }).pipe(
            rxFilter(event => event.type === HttpEventType.UploadProgress || event.type === HttpEventType.Response),
            map(event => {
                if (event.type === HttpEventType.UploadProgress) {
                    const total = event.total ?? 0;
                    return { progress: total > 0 ? Math.round((event.loaded / total) * 100) : 0, done: false };
                }
                return { progress: 100, done: true };
            })
        );
    }

    /**
     * Parse the X-Pagination response header. A missing OR malformed header yields undefined so a
     * successful response is never turned into a handled exception — callers default hasNext to false.
     */
    private parsePaginationHeader(header: string | null): PaginationResponseHeader | undefined {
        if (!header) return undefined;
        try {
            return JSON.parse(header) as PaginationResponseHeader;
        } catch {
            return undefined;
        }
    }

    /** POST /documents/filter — list a folder's files, paginated (X-Pagination header → hasNext). */
    public async filterDocuments(filter: DocumentFilter, skip: number, take: number, orderBy: DocumentSortOptions): Promise<ServiceResultModel<PaginatedListModel<DocumentModel>>> {
        try {
            const url = `${this.documentsApiPath}/filter?orderBy=${encodeURIComponent(orderBy)}&skip=${skip}&take=${take}`;
            const response = await firstValueFrom(this.httpClient.post<DocumentModel[]>(
                url,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<DocumentFilter>(filter)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            const pagination = this.parsePaginationHeader(response.headers.get('x-pagination'));
            const paginatedList: PaginatedListModel<DocumentModel> = {
                data: response.body ?? [],
                total: pagination?.total,
                skip: pagination?.skip,
                take: pagination?.take,
                hasPrevious: pagination?.hasPrevious,
                hasNext: pagination?.hasNext ?? false
            };
            return { success: true, data: paginatedList, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /**
     * Build the API `DocumentFilter` for a page/view of files from the page's `FileQueryCriteria`.
     * Shared by `getFiles` (listing) and the bulk-delete select-all path, so a "select all files"
     * delete always targets exactly the filter currently driving the visible list.
     */
    public buildDocumentFilter(criteria: FileQueryCriteria): DocumentFilter | undefined {
        const orgId = this.userSessionService.activeOrganization()?.organizationId;
        if (!orgId) return undefined;
        return {
            organizationId: orgId,
            folderId: criteria.folderId,
            searchTerm: criteria.searchTerm,
            fileTypes: criteria.fileTypes,
            permissions: criteria.permissions,
            minimumModifiedDate: criteria.modifiedAfter,
            maximumModifiedDate: criteria.modifiedBefore,
            timeZoneId: this.userSessionService.activeOrganizationSettings()?.timeZoneId
        };
    }

    /**
     * Fetch one page of files as DocumentNodeModel rows. Browse passes `{ folderId }`; search omits it
     * and passes the facets. The org timezone scopes the modified-date range to the displayed zone.
     */
    public async getFiles(criteria: FileQueryCriteria, orderBy: DocumentListSortOptions, skip: number, take: number): Promise<ServiceResultModel<PaginatedListModel<DocumentNodeModel>>> {
        const filter = this.buildDocumentFilter(criteria);
        if (!filter) {
            return this.noActiveOrganizationError();
        }

        const result = await this.filterDocuments(filter, skip, take, this.mapToDocumentSort(orderBy));
        if (!result.success || !result.data) {
            return { success: false, message: result.message, statusCode: result.statusCode };
        }

        return {
            success: true,
            data: { ...result.data, data: (result.data.data ?? []).map(d => this.mapDocumentToNode(d)) },
            statusCode: result.statusCode
        };
    }

    /**
     * PATCH /documents/{documentId} — rename a file. `name` is the full filename; the API rejects a
     * changed extension (the rename control locks it) and auto-suffixes on a sibling collision.
     */
    public async updateDocument(documentId: number, request: UpdateDocumentRequest): Promise<ServiceResultModel<DocumentModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.patch<DocumentModel>(
                `${this.documentsApiPath}/${documentId}`,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<UpdateDocumentRequest>(request)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            this.touch();
            return { success: true, data: response.body as DocumentModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    /** DELETE /documents/{documentId} — soft-delete a file with an optional audit note. */
    public async deleteDocument(documentId: number, note?: string): Promise<ServiceResultModel<DocumentModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.delete<DocumentModel>(
                `${this.documentsApiPath}/${documentId}`,
                {
                    body: note ? JSON.stringify(note) : null,
                    observe: 'response',
                    headers: DocumentsConstants.JSON_HEADERS()
                }
            ));
            this.touch();
            return { success: true, data: response.body as DocumentModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /**
     * GET /documents/{documentId} — fetch a single file's current metadata. `bustCache` evicts any
     * cached copy of this exact request first, so a scan-status poll always sees the server's latest
     * `status` (Pending → Available/Quarantined) rather than a value the 60s GET cache is still
     * holding. Only this document's entry is evicted — the folder/file list cache is untouched.
     */
    public async getDocumentById(documentId: number, bustCache: boolean = false): Promise<ServiceResultModel<DocumentModel>> {
        try {
            const requestUrl = `${this.documentsApiPath}/${documentId}`;
            if (bustCache) {
                // The cache key for a param-less GET is just its URL (see CacheInterceptor.getUniqueMapKey).
                this.cacheService.delete(requestUrl);
            }
            const response = await firstValueFrom(this.httpClient.get<DocumentModel>(
                requestUrl,
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            return { success: true, data: response.body as DocumentModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /**
     * GET /documents/{documentId}/download — resolve a short-lived URL the file is fetched from on
     * Azure (SAS for private, CDN for public). The server authorizes access and audits the grant.
     */
    public async getDownloadUrl(documentId: number): Promise<ServiceResultModel<DocumentDownloadModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.get<DocumentDownloadModel>(
                `${this.documentsApiPath}/${documentId}/download`,
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            return { success: true, data: response.body as DocumentDownloadModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    /**
     * Fetch the file bytes straight from the Azure SAS/CDN URL using the interceptor-free client (so
     * our Auth0 JWT is never sent to Azure). Returning a Blob lets the caller save it via an object URL
     * — downloading in the current tab without altering the URL (so it stays embeddable/previewable).
     * Reading a public CDN file cross-origin requires its CORS allow-header.
     */
    public async fetchDocumentBlob(url: string): Promise<ServiceResultModel<Blob>> {
        try {
            const blob = await firstValueFrom(this.blobHttpClient.get(url, { responseType: 'blob' }));
            return { success: true, data: blob, statusCode: 200 };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /**
     * POST /documents/bulk/move — move a selection of files into one destination folder in a single
     * action. Moved files adopt the destination root's permission; the API auto-suffixes on a
     * filename collision. A move that crosses the Public boundary may be rejected (422) above the
     * cross-container cap — `extractApiMessage` surfaces the server's reason. Touches so the page
     * re-fetches the affected folders.
     */
    public async bulkMoveDocuments(request: BulkMoveDocumentsRequest): Promise<ServiceResultModel<BulkActionResultModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.post<BulkActionResultModel>(
                `${this.documentsApiPath}/bulk/move`,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<BulkMoveDocumentsRequest>(request)),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            this.touch();
            return { success: true, data: response.body as BulkActionResultModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    /**
     * DELETE /documents/bulk — soft-delete a selection of files, moving them to the Trash in a single
     * action. Touches so the page re-fetches (files disappear, storage usage re-syncs).
     */
    public async bulkDeleteDocuments(request: DocumentBulkActionRequest): Promise<ServiceResultModel<BulkActionResultModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.delete<BulkActionResultModel>(
                `${this.documentsApiPath}/bulk`,
                {
                    body: JSON.stringify(APISanitizerUtilities.sanitizePayload<DocumentBulkActionRequest>(request)),
                    observe: 'response',
                    headers: DocumentsConstants.JSON_HEADERS()
                }
            ));
            this.touch();
            return { success: true, data: response.body as BulkActionResultModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    /**
     * POST /documents/trash/empty — permanently hard-delete every soft-deleted file (and now-empty
     * soft-deleted folder) in the org's Trash. The bytes are purged from blob storage, so the
     * reclaimed space frees immediately and recovery is no longer possible. Touches so the usage bar
     * re-fetches (Trash drops to zero, Free grows). The audit entry is written server-side.
     */
    public async emptyTrash(organizationId: number, note?: string): Promise<ServiceResultModel<BulkActionResultModel>> {
        try {
            const response = await firstValueFrom(this.httpClient.post<BulkActionResultModel>(
                `${this.documentsApiPath}/trash/empty`,
                JSON.stringify(APISanitizerUtilities.sanitizePayload<EmptyTrashRequest>({ organizationId, note })),
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            this.touch();
            return { success: true, data: response.body as BulkActionResultModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error, this.extractApiMessage(error));
        }
    }

    //#endregion Document API

    //#region Audit log API

    /**
     * GET /organizations/{organizationId}/document-logs — the single, org-scoped activity log
     * covering the entire Document Center (folder/file create, upload, delete, move, rename,
     * permission changes), newest-first. Acting user + note are only populated when includeDetails
     * is true and the caller holds the audit-details permission.
     */
    public async getDocumentCenterAuditLogs(organizationId: number, skip: number, take: number, includeDetails: boolean = false): Promise<ServiceResultModel<PaginatedListModel<AuditLog>>> {
        try {
            let requestUrl = `${ApiBaseUrls.CORE()}${ApiControllers.ORGANIZATIONS()}/${organizationId}/document-logs?skip=${skip}&take=${take}`;
            if (includeDetails) {
                requestUrl += `&includeDetails=true`;
            }
            const response = await firstValueFrom(this.httpClient.get<DocumentCenterAuditLogModel[]>(requestUrl, { observe: "response", headers: DocumentsConstants.JSON_HEADERS() }));
            const mappedData = AuditLogMapper.mapDocumentCenterAuditLogs(response.body ?? []);

            const paginationHeader: PaginationResponseHeader = JSON.parse(response.headers.get("x-pagination")!);
            const paginatedList: PaginatedListModel<AuditLog> = {
                data: mappedData,
                total: paginationHeader?.total,
                skip: paginationHeader?.skip,
                take: paginationHeader?.take,
                hasPrevious: paginationHeader?.hasPrevious,
                hasNext: paginationHeader?.hasNext
            };

            return { data: paginatedList, success: true, message: "Audit logs loaded successfully." };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    //#endregion Audit log API

    //#region Storage usage API

    /**
     * GET /organizations/{organizationId}/storage-usage — the org-scoped Document Center storage
     * totals (allotted, active/used, recently-deleted, free) used by the usage bar graph. A null
     * `allottedBytes`/`freeBytes` indicates an unlimited storage.
     */
    public async getStorageUsage(organizationId: number): Promise<ServiceResultModel<DocumentStorageUsageModel>> {
        try {
            const requestUrl = `${ApiBaseUrls.CORE()}${ApiControllers.ORGANIZATIONS()}/${organizationId}/storage-usage`;
            const response = await firstValueFrom(this.httpClient.get<DocumentStorageUsageModel>(
                requestUrl,
                { observe: 'response', headers: DocumentsConstants.JSON_HEADERS() }
            ));
            return { success: true, data: response.body as DocumentStorageUsageModel, statusCode: response.status };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    //#endregion Storage usage API

    //#region Compat: list rendering shape

    /**
     * Return the org's ROOT-LEVEL folders as DocumentNodeModel rows — `POST /folders/filter` with no
     * `parentFolderId` lists only the root level, not the whole tree — and merge them into the folder
     * cache. Used by the page to warm the cache and feed the move modal's destination picker.
     */
    public async getFolderTree(): Promise<ServiceResultModel<DocumentNodeModel[]>> {
        const orgId = this.userSessionService.activeOrganization()?.organizationId;
        if (!orgId) {
            return this.noActiveOrganizationError();
        }

        const result = await this.fetchAllFolders({ organizationId: orgId }, FolderSortOptions.NameAsc);
        if (!result.success || !result.data) {
            return { success: false, message: result.message, statusCode: result.statusCode };
        }

        // Merge (do NOT clear) the fetched folders into the cache. `/folders/filter` with no parent
        // returns only root-level folders, so clearing here would evict freshly-created or
        // navigated-into nested folders that createFolder/getChildren just cached — which made
        // moveFolder's source lookup intermittently miss and return a bogus "folder not found".
        for (const folder of result.data) {
            this.folderCache.set(folder.folderId, folder);
        }

        const nodes = result.data.map(f => this.folderToDocumentNode(f));
        return { success: true, data: nodes, statusCode: result.statusCode };
    }

    /**
     * Get the folders at a given parent level as DocumentNodeModel rows. Folders come from
     * POST /folders/filter, already sorted server-side. (File rows arrive with the file API.)
     */
    public async getChildren(parentId: number | undefined, orderBy: DocumentListSortOptions, permissions?: DocumentPermission[]): Promise<ServiceResultModel<DocumentNodeModel[]>> {
        try {
            const orgId = this.userSessionService.activeOrganization()?.organizationId;
            if (!orgId) {
                return this.noActiveOrganizationError();
            }

            const folderResult = await this.fetchAllFolders(
                {
                    organizationId: orgId,
                    parentFolderId: parentId,
                    permissions: permissions && permissions.length > 0 ? permissions : undefined
                },
                this.mapToFolderSort(orderBy)
            );
            if (!folderResult.success) {
                return { success: false, message: folderResult.message, statusCode: folderResult.statusCode };
            }

            const folderNodes = (folderResult.data ?? []).map(f => this.folderToDocumentNode(f));
            return { success: true, data: folderNodes, statusCode: 200 };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    /**
     * Search folders via POST /folders/filter — `searchTerm` spans all levels in the org and the
     * `permissions` filter applies when set. Folders have no file-type/date attributes, so those
     * facets don't apply here.
     */
    public async searchFolders(searchTerm: string | undefined, permissions: DocumentPermission[] | undefined, orderBy: DocumentListSortOptions): Promise<ServiceResultModel<DocumentNodeModel[]>> {
        try {
            const orgId = this.userSessionService.activeOrganization()?.organizationId;
            if (!orgId) {
                return this.noActiveOrganizationError();
            }

            const folderResult = await this.fetchAllFolders(
                {
                    organizationId: orgId,
                    searchTerm,
                    permissions: permissions && permissions.length > 0 ? permissions : undefined
                },
                this.mapToFolderSort(orderBy)
            );
            if (!folderResult.success) {
                return { success: false, message: folderResult.message, statusCode: folderResult.statusCode };
            }

            const folderNodes = (folderResult.data ?? []).map(f => this.folderToDocumentNode(f));
            return { success: true, data: folderNodes, statusCode: 200 };
        } catch (error: any) {
            return ServiceUtilities.handleServiceException(error);
        }
    }

    //#endregion Compat

    //#region Helpers (public)

    public formatBytes(bytes: number | undefined): string {
        return DocumentUtilities.formatBytes(bytes);
    }

    /**
     * Collect the IDs of every descendant folder of the given folder (for the move modal's
     * destination filter). Reads from the local folder cache — assumes getFolderTree has run.
     */
    public getDescendantFolderIds(folderId: number): Set<number> {
        const result = new Set<number>();
        const queue = [folderId];
        while (queue.length > 0) {
            const current = queue.shift()!;
            for (const folder of this.folderCache.values()) {
                if (folder.parentFolderId === current && !result.has(folder.folderId)) {
                    result.add(folder.folderId);
                    queue.push(folder.folderId);
                }
            }
        }
        return result;
    }

    //#endregion

    //#region Helpers (private)

    private folderToDocumentNode(folder: FolderModel): DocumentNodeModel {
        return {
            documentNodeId: folder.folderId,
            parentId: folder.parentFolderId,
            organizationId: folder.organizationId,
            type: DocumentNodeType.Folder,
            name: folder.name,
            permission: folder.permission,
            childCount: folder.fileCount + folder.childFolderCount,
            childFolderCount: folder.childFolderCount,
            childFileCount: folder.fileCount,
            isEmpty: folder.isEmpty,
            isLocked: folder.isLocked,
            modifiedDate: folder.lastModified,
            modifiedByName: '',
            createdDate: folder.created,
            createdByName: ''
        };
    }

    // Map the list sort to the folders API enum. Size/Type have no folder equivalent → default to Name.
    private mapToFolderSort(sort: DocumentListSortOptions): FolderSortOptions {
        switch (sort) {
            case DocumentListSortOptions.NameDesc: return FolderSortOptions.NameDesc;
            case DocumentListSortOptions.LastModifiedDesc: return FolderSortOptions.LastModifiedDesc;
            case DocumentListSortOptions.LastModifiedAsc: return FolderSortOptions.LastModifiedAsc;
            case DocumentListSortOptions.NameAsc:
            default:
                return FolderSortOptions.NameAsc;
        }
    }

    // Map the list sort to the files API enum.
    private mapToDocumentSort(sort: DocumentListSortOptions): DocumentSortOptions {
        switch (sort) {
            case DocumentListSortOptions.NameDesc: return DocumentSortOptions.NameDesc;
            case DocumentListSortOptions.LastModifiedAsc: return DocumentSortOptions.LastModifiedAsc;
            case DocumentListSortOptions.LastModifiedDesc: return DocumentSortOptions.LastModifiedDesc;
            case DocumentListSortOptions.SizeAsc: return DocumentSortOptions.SizeAsc;
            case DocumentListSortOptions.SizeDesc: return DocumentSortOptions.SizeDesc;
            case DocumentListSortOptions.TypeAsc: return DocumentSortOptions.TypeAsc;
            case DocumentListSortOptions.TypeDesc: return DocumentSortOptions.TypeDesc;
            case DocumentListSortOptions.NameAsc:
            default:
                return DocumentSortOptions.NameAsc;
        }
    }

    // Map an API file to the grid's DocumentNodeModel. A file inherits its folder's permission,
    // resolved from the cache.
    private mapDocumentToNode(doc: DocumentModel): DocumentNodeModel {
        return {
            documentNodeId: doc.documentId,
            parentId: doc.folderId,
            organizationId: doc.organizationId,
            type: DocumentNodeType.File,
            name: doc.name,
            fileTypeCategory: doc.fileTypeCategory,
            mimeType: doc.contentType,
            sizeBytes: doc.sizeBytes,
            status: doc.status,
            permission: this.folderCache.get(doc.folderId)?.permission ?? DocumentPermission.AdminOnly,
            modifiedDate: doc.lastModified,
            modifiedByName: '',
            createdDate: doc.created,
            createdByName: ''
        };
    }

    /**
     * Total file count across a folder's subtree, summed from the cache. Drives the cross-container
     * cap pre-check; the backend remains the source of truth when the cache is incomplete.
     */
    private countDescendantFiles(folderId: number): number {
        const descendants = this.getDescendantFolderIds(folderId);
        descendants.add(folderId);
        let total = 0;
        for (const id of descendants) {
            total += this.folderCache.get(id)?.fileCount ?? 0;
        }
        return total;
    }

    private crossesPublicBoundary(current: DocumentPermission, next: DocumentPermission): boolean {
        const isPublic = (p: DocumentPermission) => p === DocumentPermission.Public;
        return isPublic(current) !== isPublic(next);
    }

    private touch(): void {
        this.version.update(v => v + 1);
    }

    //#endregion
}
